/**
 * <HighlightFrame> — la bordure en dégradé des temps forts (molecule, Brand core V13 chap. 6).
 *
 * Le jaune signature, puis les nœuds de Zoé, Tina, Sam et Abou, à 120°. C'est le seul rôle de
 * ces cinq couleurs dans l'interface, et le dégradé ne se pose que sur **un temps fort** :
 *
 *  - ✅ la carte de la **prochaine action** (la prochaine séance, sur l'accueil parent) ;
 *  - ✅ une **carte d'événement** (le stage de la Toussaint, une offre datée).
 *  - ❌ une carte ordinaire, une liste, un bouton, un texte, le logo, un champion. Un écran qui
 *    en porte deux n'en met plus aucun en avant : il y en a **au plus un par écran**.
 *
 * En code : une vue en dégradé posée SOUS l'enfant, qui déborde de tous les côtés de l'épaisseur
 * du cadre — 1,5 px sur un téléphone, 2 px à partir de `md` — et suit son rayon. La carte garde donc sa taille et son ombre — l'encadrement ne rogne pas son
 * contenu, et une carte déjà câblée s'encadre sans être redessinée.
 */
import React from "react"
import { StyleSheet as RNStyleSheet, View, type StyleProp, type ViewStyle } from "react-native"
import { LinearGradient } from "expo-linear-gradient"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { useGradientAnchors } from "../../hooks/useGradientAnchors"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { championRamp, highlightFrame } from "../../theme/tokens/effects"

/**
 * Une valeur par largeur d'écran, à partir de l'épaisseur du liseré à cette largeur : Unistyles
 * résout l'objet au point de rupture courant (une media query sur le web).
 */
function byFrameWidth(value: (width: number) => number) {
    const { xs, md } = highlightFrame.width
    return { xs: value(xs), md: value(md) }
}

/** Le jaune, puis les nœuds de Zoé, Tina, Sam et Abou — arrêts et angle : `championRamp`. */
const FRAME_COLORS: SemanticColorKey[] = ["accent", "champion-zoe", "champion-tina", "champion-sam", "champion-abou"]

export type HighlightFrameProps = {
    /** La surface encadrée — une `<Card>` / `<CardList>` déjà câblée, inchangée. */
    children: React.ReactNode
    /**
     * Rayon de la surface encadrée, en px. Défaut : le rayon d'une carte (`radius.lg`). Le cadre
     * prend ce rayon plus son épaisseur, pour que les deux courbes restent parallèles.
     */
    radius?: number
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function HighlightFrame(props: HighlightFrameProps) {
    const { children, radius, style, testID } = props

    const { theme } = useUnistyles()
    const innerRadius = radius ?? theme.radius.lg

    // `expo-linear-gradient` ne prend pas d'angle mais deux points en fractions de la boîte : des
    // ancres figées ne valaient 120° que sur un carré (99,6° sur la carte des prochaines séances,
    // 1092 × 317). Le crochet mesure la boîte et recalcule les ancres.
    const { anchors, onLayout } = useGradientAnchors(championRamp.angle)

    return (
        <View testID={testID} style={style}>
            {/* Le cadre est une VUE (Unistyles y résout rayon et position) qui rogne le dégradé :
                `<LinearGradient>` n'est pas un composant patché par Unistyles, une feuille du
                thème posée sur lui n'est jamais résolue — elle sortait sans rayon ni hauteur. */}
            <View style={styles.frame(innerRadius)} pointerEvents="none" onLayout={onLayout}>
                <LinearGradient
                    colors={FRAME_COLORS.map((key) => theme.colors[key]) as [string, string, ...string[]]}
                    locations={championRamp.stops}
                    start={anchors.start}
                    end={anchors.end}
                    style={RNStyleSheet.absoluteFill}
                />
            </View>
            {children}
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    frame: (innerRadius: number) => ({
        position: "absolute" as const,
        top: byFrameWidth((width) => -width),
        left: byFrameWidth((width) => -width),
        right: byFrameWidth((width) => -width),
        bottom: byFrameWidth((width) => -width),
        borderRadius: byFrameWidth((width) => innerRadius + width),
        overflow: "hidden" as const,
    }),
}))
